import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import PermMediaOutlinedIcon from '@mui/icons-material/PermMediaOutlined';
import PlaylistRemoveRoundedIcon from '@mui/icons-material/PlaylistRemoveRounded';
import TranscriptIcon from '@mui/icons-material/RecordVoiceOver';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { ActionButton } from '@pages/ActionButton';
import type { ZeroResultReason } from '@pages/Search/utils/zeroResultReason';
import { SegmentType } from '@stores/Library/LibrarySearch';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { trackingStore } from '@stores/Tracking';
import { observer } from 'mobx-react';
import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import css from './NoMomentsFound.module.scss';

/** Longest query we'll echo back in the heading before eliding the middle. */
const MAX_ECHOED_QUERY = 60;

/**
 * Echo the user's own words back at them, which is half the point of this state —
 * a heading naming the query is proof the search ran and finished.
 *
 * Elides the *middle* rather than the tail: a long query's distinguishing words
 * are as often at the end as the start, and a heading ending in "…" reads as
 * still-loading, which is exactly the impression this whole state exists to undo.
 */
function echoQuery(text: string): string {
  const trimmed = text.trim();
  if (trimmed.length <= MAX_ECHOED_QUERY) return trimmed;
  const half = Math.floor((MAX_ECHOED_QUERY - 1) / 2);
  return `${trimmed.slice(0, half).trimEnd()}…${trimmed.slice(-half).trimStart()}`;
}

/** The mode to offer as the way out of a fruitless search, and how to say it. */
const OTHER_MODE: Partial<
  Record<SegmentType, { type: SegmentType; label: string; icon: ReactNode }>
> = {
  visual: {
    type: 'transcript',
    label: "Search what's said instead",
    icon: <TranscriptIcon className={css.ctaIcon} />,
  },
  transcript: {
    type: 'visual',
    label: "Search what's shown instead",
    icon: <VisibilityOutlinedIcon className={css.ctaIcon} />,
  },
  // Unified already searches both, so there is nowhere to send the user. No CTA
  // beats a CTA that does nothing.
};

/**
 * One line for every mode. It used to branch three ways — shows / says / matches —
 * which was symmetrical but bought nothing: the mode is already named by the CTA
 * offering the other one, and this is now the rarest state on the page, since visual
 * search applies no score threshold and so always returns something against indexed
 * media.
 */
const NO_MATCH_LINE = 'Nothing in your media matches this.';

type Content = {
  icon: ReactNode;
  heading: string;
  line: string;
  action?: { label: string; icon: ReactNode; onClick: () => void };
};

/**
 * What the Moments page says when a search settles with nothing to show.
 *
 * Swapped in for `MomentGrid` by `SearchPage` on `multiLibSearch.zeroResultReason`,
 * the same way `TranscriptSearchSuggestion` is swapped in on
 * `shouldSuggestTranscriptSearch` — and built to be its structural twin, because
 * it occupies the same slot doing the same job. Only the glyph, the two lines and
 * the action change between them.
 *
 * Every variant offers exactly one action, and it is always the thing that actually
 * unblocks *that* reason: switch modes when the words missed, add media when there is
 * none, drop a library we can't reach, go and watch the media that isn't ready yet. A
 * state that only explains is still a dead end.
 */
export const NoMomentsFound = observer(function NoMomentsFound({
  reason,
  onAddMedia,
}: {
  reason: ZeroResultReason;
  /**
   * Opens the Add-media modal, targeting the given library. Owned by `SearchPage`,
   * which mounts the modal.
   *
   * The target matters: "these libraries are empty" is a fact about the *search
   * scope*, which needn't contain the default library the modal would otherwise
   * pick. Adding media to the default could leave this very state on screen with the
   * media in a library the search isn't looking at.
   */
  onAddMedia: (libraryId?: string) => void;
}) {
  const libraryManager = useLibraryManager();
  const search = libraryManager.multiLibSearch;
  const navigate = useNavigate();

  // No progress indicator here, deliberately. Real progress only exists per media
  // while it is `processing` (see `getDisplayProgress`); queued media have nothing
  // genuine to show, and rendering a guess invents a number and invites the user to
  // time their next action by it. The library page is where the genuine per-media
  // state lives, so this state sends them there instead.

  // One event per distinct zero-result outcome. Keyed on the reason plus what was
  // asked, so re-rendering doesn't re-fire but changing the query or the mode does —
  // each of those is a separate search that came back empty.
  //
  // Safe to fire from render-time state because the resolver withholds every reason
  // while a search is in flight: this component only exists once the answer is
  // settled, so there is no half-finished search to report.
  const resultType = search.resultType;
  const query = search.request?.text?.trim() ?? '';
  const libraryCount = libraryManager.selectedLibrariesToSearch.length;
  // Gated on `settled`, not just on being mounted. A scope-derived reason renders
  // during the 400ms debounce, so without this every keystroke after a zero-result
  // search emits an event — "c", "ca", "cat" — for queries that were never sent.
  // The distribution is the entire point of this metric, so seeding it with
  // half-typed phantom searches would poison exactly what it exists to measure.
  const settled = search.allLibrariesSettledSearch;
  useEffect(() => {
    if (!settled) return;
    trackingStore.searchReturnedNothing({ reason, resultType, libraryCount });
    // `query` is a dependency but deliberately not a property — it would put raw
    // user text in the event, and `Search Performed` already carries the query for
    // anyone who needs to join the two.
    // `libraryCount` is a dep as well as a property: re-running the same query after
    // changing the scope is a distinct zero-result search, and "how often is the
    // reason `empty-scope` / `unreachable`" is the whole point of the event — those
    // are precisely the reasons a user reacts to by changing their scope. Without it
    // the metric would miss the retry and undercount the dimension it exists for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reason, resultType, query, libraryCount, settled]);

  /**
   * Re-run the search. A settled search never re-fires on its own, so a retry has to
   * be asked for.
   *
   * Deliberately does **not** refresh library stats. It used to, for the `processing`
   * state's benefit, but that state now offers the library instead of a retry, leaving
   * `search-failed` as the only caller — and there a refresh is actively harmful.
   * `LibraryStore` is a DataFrame with `keepDataOnFetchError = false`, so on the very
   * outage that produced `search-failed`, refetching would null every library's `data`,
   * empty the scope, and drop the user back on the blank page this state exists to
   * replace, with the button that got them there now gone.
   *
   * Libraries already sitting at `data === null` are refetched, since that is the only
   * way they ever come back.
   */
  const searchAgain = () => {
    for (const id of libraryManager.selectedLibraryIdsToSearch) {
      const library = libraryManager.libraryById(id);
      if (!library) continue;
      // Failures land on the stores' own `error`, which this state already reads —
      // rethrowing would only produce an unhandled rejection.
      if (!library.data) library.update().catch(() => {});
      library.search.update().catch(() => {});
    }
  };

  const content = buildContent();

  return (
    <div className={css.root}>
      <div className={css.iconCircle}>{content.icon}</div>
      <div className={css.text}>
        <h1>{content.heading}</h1>
        <span>{content.line}</span>
      </div>
      {content.action && (
        <ActionButton variant="primary" onClick={content.action.onClick}>
          <span className={css.ctaContent}>
            {content.action.icon}
            {content.action.label}
          </span>
        </ActionButton>
      )}
    </div>
  );

  function buildContent(): Content {
    switch (reason) {
      case 'no-scope':
        return {
          icon: <LibraryBooksOutlinedIcon className={css.icon} />,
          heading: 'No libraries selected',
          line: 'Pick at least one library to search.',
          action: {
            label: 'Search all libraries',
            icon: <LibraryBooksOutlinedIcon className={css.ctaIcon} />,
            // Two things this must not do, both learned from the picker.
            //
            // `setSelectedLibraries` rather than `selectAllLibrariesToSearch`, because
            // only the former turns `multiLibrarySelection` on for a multi-library
            // result. Without it the scope holds N libraries while the picker's toggle
            // reads off, so its rows render as single-pick and "Deselect all" isn't
            // even shown.
            //
            // And empty libraries are filtered out, because the picker treats that as
            // load-bearing: `handleGroupSelection` routes "Select all" through
            // `disabledReasonFor` precisely so a bulk action can't sweep up rows it
            // renders inert. Handing someone a six-library scope the picker would
            // never have produced — five of them empty — then blaming those libraries
            // in the next breath is worse than the state we started from. Falls back
            // to everything when nothing has media, since an empty scope is the one
            // outcome this button must not produce.
            onClick: () => {
              const withMedia = libraryManager.libraries.filter((l) => !l.isEmpty);
              const pick = withMedia.length > 0 ? withMedia : libraryManager.libraries;
              libraryManager.setSelectedLibraries(pick.map((l) => l.libraryId));
            },
          },
        };

      case 'empty-scope': {
        const empties = search.emptyLibrariesInScope;
        const many = empties.length > 1;
        return {
          icon: <PermMediaOutlinedIcon className={css.icon} />,
          heading: 'Nothing to search yet',
          line: many
            ? "The libraries you're searching are empty. Add media and your moments will show up here."
            : "The library you're searching is empty. Add media and your moments will show up here.",
          action: {
            label: 'Add media',
            icon: <AddRoundedIcon className={css.ctaIcon} />,
            // Target the empty library itself when there is exactly one, so the
            // media lands where the search is looking. With several, leave it to the
            // modal's own picker rather than guessing which one they meant.
            onClick: () => onAddMedia(many ? undefined : empties[0]?.libraryId),
          },
        };
      }

      // Nothing is searchable *yet*. The only real answer is to wait, so this is
      // the one variant that shows progress instead of a button — and the button
      // it becomes is the one that gets the user out, because the stats behind
      // this state are never refreshed on their own.
      case 'processing':
        return {
          icon: <HourglassTopRoundedIcon className={css.icon} />,
          // Three signals reach this state and they support different claims, so the
          // heading says only as much as the one we actually have:
          //
          //  - a live batch means bytes are moving, and the user can see that anyway
          //  - `queued`/`processing` in stats means the backend has the file and is
          //    working on it
          //  - anything else is only "this row is not terminal", which covers a stalled
          //    upload and an abandoned one as readily as a healthy wait. Claiming
          //    "processing" there is a guess, and it was the wrong one often enough to
          //    be worth not making.
          heading: search.isTransferringInScope
            ? 'Your media is still uploading'
            : search.backendWorkingLibrariesInScope.length > 0
              ? 'Your media is still processing'
              : "Your media isn't ready to search yet",
          // True whichever of the three it is — including the case where it will never
          // land, which is exactly why the offer is the library rather than a retry.
          // Also careful not to promise an update that never comes: nothing refreshes
          // library stats or re-runs a settled search when processing finishes.
          line: 'Check your library to see where it has got to.',
          // The library, not a re-run. Waiting is the actual answer here, and the
          // library page is where the wait is legible — real per-media status, and the
          // failures if any turn up. "Search again" asked the user to guess when to
          // press it, and told them nothing when they guessed early.
          action: {
            label: 'Go to library',
            icon: <PermMediaOutlinedIcon className={css.ctaIcon} />,
            onClick: () => {
              const only = search.soleActiveUploadLibraryIdInScope;
              navigate(only ? `/library/${only}` : '/libraries');
            },
          },
        };

      case 'nothing-usable': {
        const failed = search.failedMediaLibrariesInScope;
        const only = failed.length === 1 ? failed[0] : undefined;
        return {
          icon: <ErrorOutlineRoundedIcon className={css.icon} />,
          // "couldn't be processed" against `processing`'s "is still processing":
          // the auxiliary verb is doing the work, and it has to, because the two
          // states are adjacent and mean opposite things — one resolves itself, this
          // one never will.
          heading: "Your media couldn't be processed",
          line: "None of it finished, so there's nothing to search.",
          // The media is already here and it failed, so adding more is not the
          // answer — send them to the cards, where the failed ones show their status
          // and their retry and clear actions. Only possible when one library is at
          // fault; with several there is no single place to open, so fall back to the
          // offer that at least works.
          action: only
            ? {
                label: 'Open library',
                icon: <PermMediaOutlinedIcon className={css.ctaIcon} />,
                onClick: () => navigate(`/library/${only.libraryId}`),
              }
            : {
                label: 'Add media',
                icon: <AddRoundedIcon className={css.ctaIcon} />,
                onClick: () => onAddMedia(),
              },
        };
      }

      case 'unreachable': {
        const ids = search.unsearchableLibraryIdsInScope;
        // We can only name a library we still hold a store for — an access revoked
        // mid-session. A stale id rehydrated from localStorage after the library
        // was deleted or unshared has no store and therefore no name, so that case
        // can only be counted.
        const named = ids.length === 1 ? libraryManager.libraryById(ids[0])?.data?.name : undefined;
        const one = ids.length === 1;
        return {
          icon: <SearchOffRoundedIcon className={css.icon} />,
          heading: named
            ? `Couldn't search “${named}”`
            : one
              ? "Couldn't search one library"
              : `Couldn't search ${ids.length} libraries`,
          // Two possibilities and no way to tell them apart: the search endpoint's
          // access check is a membership test, so a library that was deleted and
          // one that was never ours answer identically. "No longer" is not a
          // softener — an id only reaches the scope by being selected, which
          // required access at the time.
          line: one
            ? 'It may have been deleted, or you may no longer have access.'
            : 'They may have been deleted, or you may no longer have access.',
          action: {
            label: one ? 'Remove it from search' : 'Remove them from search',
            icon: <PlaylistRemoveRoundedIcon className={css.ctaIcon} />,
            // `dropLibrariesFromSearch`, not a loop of `deselectLibraryToSearch`:
            // these may be everything in the scope, and emptying it from here would
            // leave a blank picker with nothing to restore it — see that method.
            // It also writes through the localStorage reaction on the selection, so
            // a stale scope heals for good rather than greeting the user again
            // tomorrow.
            onClick: () => libraryManager.dropLibrariesFromSearch(ids),
          },
        };
      }

      case 'search-failed':
        return {
          icon: <ErrorOutlineRoundedIcon className={css.icon} />,
          heading: "Search couldn't finish",
          line: 'Something went wrong while searching your libraries.',
          action: {
            label: 'Try again',
            icon: <RefreshRoundedIcon className={css.ctaIcon} />,
            onClick: searchAgain,
          },
        };

      case 'no-match': {
        const query = search.request?.text?.trim();
        const other = OTHER_MODE[search.resultType];
        // Only offer a mode the scope can actually answer in. Both directions have a
        // way of failing, and each produces a button that visibly does nothing useful.
        //
        // → transcript needs words: `hasSearchTerms` requires `request.text` in that
        //   mode, so switching an image- or clip-led search makes it false,
        //   `SearchPage` drops the whole results block, and the page falls back to its
        //   landing copy. The button reads as losing the search.
        // → visual needs a scope that indexes visuals: on an all-transcript-only scope,
        //   switching lands in `shouldSuggestTranscriptSearch`, whose only button
        //   switches straight back here. A two-state ping-pong with no way out.
        const targetCanRun =
          other &&
          (other.type !== 'transcript' || !!query) &&
          (other.type !== 'visual' || !search.allScopeIsTranscriptOnly);
        return {
          icon: <SearchOffRoundedIcon className={css.icon} />,
          // An image- or clip-led search has no words to quote, so it falls back
          // to the plain heading rather than an empty pair of quotes.
          heading: query ? `No moments match “${echoQuery(query)}”` : 'No moments found',
          line: NO_MATCH_LINE,
          action:
            other && targetCanRun
              ? {
                  label: other.label,
                  icon: other.icon,
                  onClick: () => search.setResultType(other.type),
                }
              : undefined,
        };
      }
    }
  }
});
